import { useState, useCallback } from 'react';
import { sdkRequest } from '@eventbrite/legacy-infra.http';
import type { FollowActionApiResponse } from '@/components/OrganizerCard/types';
import { FOLLOW_ACTION, UNFOLLOW_ACTION, type FollowAction } from '@/components/_shared/buttons/FollowButton/types';

export interface FollowActionResult {
  success: boolean;
}

export interface UseFollowActionsResult {
  follow: (organizerId: string) => Promise<FollowActionResult>;
  unfollow: (organizerId: string) => Promise<FollowActionResult>;
  followActionLoading: boolean;
}

export const useFollowActions = (): UseFollowActionsResult => {
  const [loading, setLoading] = useState(false);

  const performFollowAction = useCallback(
    async (organizerId: string, action: FollowAction): Promise<FollowActionResult> => {
      if (!organizerId) {
        console.error(`Failed to ${action} organizer: Organizer ID is required`);
        return { success: false };
      }

      try {
        setLoading(true);
        const response: FollowActionApiResponse = await sdkRequest(
          `/destination/organizers/${organizerId}/${action}/`,
          {
            method: 'POST',
          },
          process.env.NEXT_PUBLIC_PROXY_API_V3 ? '/e' : '',
        );

        return {
          success: response.action_performed,
        };
      } catch (error) {
        console.error(`Error ${action}ing organizer:`, error);
        return { success: false };
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const follow = useCallback(
    (organizerId: string) => performFollowAction(organizerId, FOLLOW_ACTION),
    [performFollowAction],
  );

  const unfollow = useCallback(
    (organizerId: string) => performFollowAction(organizerId, UNFOLLOW_ACTION),
    [performFollowAction],
  );

  return {
    follow,
    unfollow,
    followActionLoading: loading,
  };
};
